import type { AuthGuard } from '@/lib/jwt'
import type { SidebarNavGroupChild } from '@/types/sidebar'

export type SidebarMenuLink = {
  kind: 'link'
  to: string
  label: string
  icon: string
  exact?: boolean
  sectionPrefix?: string
}

export type SidebarMenuGroup = {
  kind: 'group'
  label: string
  icon: string
  iconActive: string
  children: SidebarNavGroupChild[]
}

export type SidebarMenuItem = SidebarMenuLink | SidebarMenuGroup

const afiliadosGroup: SidebarMenuGroup = {
  kind: 'group',
  label: 'Afiliados',
  icon: 'mdi:handshake-outline',
  iconActive: 'mdi:handshake',
  children: [
    {
      to: '/afiliados',
      label: 'Meus Afiliados',
      icon: 'mdi:account-group-outline',
      sectionPrefix: '/afiliados',
    },
    {
      to: '/sou-afiliado',
      label: 'Sou Afiliado',
      icon: 'mdi:account-star-outline',
      sectionPrefix: '/sou-afiliado',
    },
  ],
}

const distribuidoresGroup: SidebarMenuGroup = {
  kind: 'group',
  label: 'Distribuidores',
  icon: 'mdi:handshake-outline',
  iconActive: 'mdi:handshake',
  children: [
    {
      to: '/meus-distribuidores',
      label: 'Meus Distribuidores',
      icon: 'mdi:account-supervisor-outline',
      sectionPrefix: '/meus-distribuidores',
    },
    {
      to: '/sou-distribuidor',
      label: 'Sou Distribuidor',
      icon: 'mdi:account-star-outline',
      sectionPrefix: '/sou-distribuidor',
    },
  ],
}

/** Menu da instituição (e do admin com instituição selecionada). */
export const INSTITUTION_SIDEBAR_MENU_ITEMS: SidebarMenuItem[] = [
  { kind: 'link', to: '/home', label: 'Início', icon: 'mdi:home', exact: true },
  {
    kind: 'link',
    to: '/gateway-pagamento',
    label: 'Gateway',
    icon: 'mdi:credit-card-outline',
    exact: true,
  },
  { kind: 'link', to: '/campanhas', label: 'Campanhas', icon: 'mdi:bullhorn' },
  { kind: 'link', to: '/shop', label: 'Shop', icon: 'mdi:shopping-outline', exact: true },
  afiliadosGroup,
  distribuidoresGroup,
  {
    kind: 'link',
    to: '/clientes/lista',
    label: 'Clientes',
    icon: 'mdi:account-multiple-outline',
    sectionPrefix: '/clientes',
  },
  {
    kind: 'link',
    to: '/pedidos/lista',
    label: 'Pedidos',
    icon: 'mdi:receipt-text-outline',
    sectionPrefix: '/pedidos',
  },
  {
    kind: 'link',
    to: '/conversoes-meta',
    label: 'Conversões',
    icon: 'mdi:chart-timeline-variant',
  },
  {
    kind: 'link',
    to: '/buscar-ganhador',
    label: 'Ganhadores',
    icon: 'mdi:trophy-outline',
    exact: true,
  },
  {
    kind: 'link',
    to: '/colaboradores',
    label: 'Colaboradores',
    icon: 'mdi:account-tie-outline',
    exact: true,
  },
]

/** Menu exclusivo da conta de administração (admin sem instituição selecionada). */
export const ADMIN_ADMINISTRATION_MENU_ITEMS: SidebarMenuLink[] = [
  {
    kind: 'link',
    to: '/clientes/lista',
    label: 'Clientes',
    icon: 'mdi:account-multiple-outline',
    sectionPrefix: '/clientes',
  },
  {
    kind: 'link',
    to: '/home-drftadmin',
    label: 'Início',
    icon: 'mdi:home',
    exact: true,
  },
  {
    kind: 'link',
    to: '/dash-gerencial',
    label: 'Dash Gerencial',
    icon: 'mdi:chart-box-outline',
    exact: true,
  },
  {
    kind: 'link',
    to: '/custos-operacionais',
    label: 'Custos operacionais',
    icon: 'mdi:cash-minus',
    exact: true,
  },
  {
    kind: 'link',
    to: '/termos-politicas',
    label: 'Termos e Políticas',
    icon: 'mdi:file-document-edit-outline',
  },
  {
    kind: 'link',
    to: '/splits-globais',
    label: 'Splits Globais',
    icon: 'mdi:chart-pie',
    exact: true,
  },
  {
    kind: 'link',
    to: '/avatar-adm',
    label: 'Avatar ADM',
    icon: 'mdi:account-edit-outline',
    exact: true,
  },
  {
    kind: 'link',
    to: '/inteligencia-artificial',
    label: 'Inteligência artificial',
    icon: 'mdi:robot-happy-outline',
    exact: true,
  },
  {
    kind: 'link',
    to: '/checklist',
    label: 'Checklist',
    icon: 'mdi:format-list-checks',
    exact: true,
  },
  {
    kind: 'link',
    to: '/monitoramento',
    label: 'Monitoramento',
    icon: 'mdi:monitor-dashboard',
    exact: true,
  },
]

export function isAdminAdministrationSidebar(
  guard: AuthGuard | null,
  institutionId: string | null
): boolean {
  return guard === 'admin' && institutionId === null
}

export const INSTITUTION_MANAGERIAL_DASHBOARD_MENU_ITEM: SidebarMenuLink = {
  kind: 'link',
  to: '/dashboard-empresarial',
  label: 'Dashboard empresarial',
  icon: 'mdi:chart-box-outline',
  exact: true,
}

export type SidebarMenuOptions = {
  canViewManagerialDashboard?: boolean
}

export function canShowInstitutionManagerialDashboardMenu(
  guard: AuthGuard | null,
  isCollaborator: boolean,
  hasActiveAccess: boolean
): boolean {
  return guard === 'institution' && !isCollaborator && hasActiveAccess
}

export function resolveSidebarMenuItems(
  guard: AuthGuard | null,
  institutionId: string | null,
  options: SidebarMenuOptions = {}
): SidebarMenuItem[] {
  if (!guard) return []
  if (isAdminAdministrationSidebar(guard, institutionId)) {
    return ADMIN_ADMINISTRATION_MENU_ITEMS
  }

  if (options.canViewManagerialDashboard && guard === 'institution') {
    const homeItem = INSTITUTION_SIDEBAR_MENU_ITEMS[0]
    if (homeItem) {
      return [
        homeItem,
        INSTITUTION_MANAGERIAL_DASHBOARD_MENU_ITEM,
        ...INSTITUTION_SIDEBAR_MENU_ITEMS.slice(1),
      ]
    }
  }

  return INSTITUTION_SIDEBAR_MENU_ITEMS
}

/** Rótulos de primeiro nível do menu (links e grupos) para testes e diagnóstico. */
export function listSidebarTopLevelLabels(items: SidebarMenuItem[]): string[] {
  return items.map((item) => item.label)
}

/** Rotas de links de primeiro nível (grupos não incluídos). */
export function listSidebarTopLevelPaths(items: SidebarMenuItem[]): string[] {
  return items.flatMap((item) => (item.kind === 'link' ? [item.to] : []))
}
